import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasEnglish, localeOf, localize, splitLocale } from '../lib/locale-paths.ts';
import { PX } from '../lib/paper-copy.ts';
import { PT } from '../lib/partners-copy.ts';
import { PLink as Link } from './PLink.tsx';  /* 英文 / 繁体网址上的站内链接留在同一种语言（lib/locale-paths.ts） */
import { dropPrerender } from '../lib/prerender-dom.ts';
import { L, STATUS, STATUS_EXT_LISTED, type Status } from '../lib/landing-copy.ts';
import { useExtStoreUrl } from '../lib/ext-store.ts';
import { loginHref } from '../lib/guest.ts';
import { tx, UI, LANGS, type Lang } from '../lib/i18n.ts';
import { useApp } from '../state/app.tsx';
import { Icon } from './Icon.tsx';

/**
 * 公开页面（首页、功能介绍、视频介绍）共用的那一圈：顶栏、语言切换、页脚、状态标签。
 *
 * ════════════════════════════════════════════════════════
 * 为什么抽出来，而不是每页抄一份
 * ════════════════════════════════════════════════════════
 * 这三页都不在应用外框里（`Shell.tsx` 那个带侧栏的），所以各自要有一套顶栏和页脚。
 * 抄三份的必然结果是某天只改了一处 —— 而这三页是**没登录的人唯一看到的东西**，
 * 顶栏里少一个入口或者页脚里挂着一条已经不存在的链接，是第一印象里最糟的一类错。
 *
 * 这个工程里同一类错已经出过几次（「两份要手动对齐的清单」），
 * 所以这里直接不给它机会。
 */

/** 功能状态标签。文案在 landing-copy 的 STATUS，只有一份 */
export function StatusTag({ s, lang }: { s: Status; lang: Lang }): React.ReactElement {
  /* 扩展上架了（运维后台填了商店链接）：「扩展即将上架」换成「要装浏览器扩展」 */
  const listed = Boolean(useExtStoreUrl());
  return <span className={'lp-st ' + s}>{tx(s === 'ext' && listed ? STATUS_EXT_LISTED : STATUS[s], lang)}</span>;
}

/** 简 / 繁 / EN。公开页面上不能只有一个地球图标 —— 第一次来的人不会去点它 */
export function LangSwitch(): React.ReactElement {
  const app = useApp();
  const loc = useLocation();
  const navigate = useNavigate();
  /* 在分语言的那几页上（lib/locale-paths.ts），换语言 = 换到那种语言的网址。
     网址和语言对不上的话，分享出去的链接打开是另一种语言，搜索引擎也会把两份当成重复页 */
  const { base } = splitLocale(loc.pathname);
  const pick = (id: Lang): void => {
    void app.setLang(id);
    if (hasEnglish(base)) navigate(localize(base, localeOf(id).prefix) + loc.hash);
  };
  return (
    <span className="seg lp-lang" role="group" aria-label={tx(UI.language, app.lang)}>
      {LANGS.map(([id, name]) => (
        <button
          key={id}
          type="button"
          aria-pressed={app.lang === id}
          title={name}
          onClick={() => pick(id)}
        >
          {id === 'zh-CN' ? '简' : id === 'zh-HK' ? '繁' : 'EN'}
        </button>
      ))}
    </span>
  );
}

export interface LandingShellProps {
  /** 顶栏中间那几个锚点。首页给自己的章节，其余两页给页面之间的跳转 */
  anchors: ReadonlyArray<{ to: string; label: string; anchor?: boolean }>;
  /** 注册之后去哪 */
  signupTo?: string | undefined;
  children: React.ReactNode;
}

export function LandingShell({ anchors, signupTo, children }: LandingShellProps): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const signup = loginHref(signupTo ?? '/p/home');

  /* 真页面已经画进 #root 了：把构建时预渲染的那一份拿掉（lib/prerender-dom.ts）。
     layout effect 在浏览器绘制之前跑，所以两份不会同时出现在屏幕上 */
  useLayoutEffect(() => { dropPrerender(); }, []);

  /* 浏览器语言不是中文、却打开了简体网址的人（比如从 Product Hunt 点进 jyfkws.com）：顶上给一条去英文版的链接。
     **不自动跳转**：搜索引擎的爬虫也是英文浏览器，自动跳走的话它永远看不到简体那一份。
     放在 effect 里判断，构建时的预渲染里不会有这一条 */
  const loc = useLocation();
  const { locale, base } = splitLocale(loc.pathname);
  const [offerEn, setOfferEn] = useState(false);
  useEffect(() => {
    const nav = typeof navigator !== 'undefined' ? (navigator.languages?.[0] ?? navigator.language ?? '') : '';
    setOfferEn(locale.prefix === '' && hasEnglish(base) && nav !== '' && !/^zh/i.test(nav));
  }, [locale.prefix, base]);

  return (
    <div className="lp">
      {/* 跳过导航直达正文。键盘和读屏用户的第一个 Tab 停在这里 */}
      <a className="lp-skip" href="#lp-main">{tx(L.skip, lang)}</a>
      {offerEn ? (
        <div className="banner info" role="note" lang="en" style={{ margin: 0, borderRadius: 0, justifyContent: 'center' }}>
          <span>This page is in Chinese. <a href={localize(base, '/en') + loc.hash}>View it in English →</a></span>
        </div>
      ) : null}

      <header className="lp-nav">
        <Link to="/" className="lp-brand">
          <span className="logo"><Icon name="shield" style="line" /></span>
          <span>{tx(UI.appName, lang)}</span>
        </Link>
        <nav className="lp-anchors" aria-label={tx(L.navLabel, lang)}>
          {anchors.map(a => (
            /* 同页锚点用 <a href="#x">（浏览器自己滚过去），跨页用 <Link>
               —— 用错的话要么整页重载，要么点了没反应 */
            a.anchor === true
              ? <a key={a.to} href={a.to}>{a.label}</a>
              : <Link key={a.to} to={a.to}>{a.label}</Link>
          ))}
        </nav>
        <span className="lp-nav-r">
          <LangSwitch />
          <Link className="btn sm" to="/p/home">{tx(L.enter, lang)}</Link>
          <Link className="btn sm primary" to={signup}>{tx(L.signin, lang)}</Link>
        </span>
      </header>

      <main id="lp-main">{children}</main>

      <footer className="lp-foot">
        <span>© {new Date().getFullYear()} {tx(UI.appName, lang)} · jyfkws.com</span>
        <span>{tx(L.footNote, lang)}</span>
        <span className="lp-foot-l">
          <Link to="/features">{tx(L.navFeatures, lang)}</Link>
          <Link to="/docs">{lang === 'en' ? 'Guide' : lang === 'zh-HK' ? '使用教程' : '使用教程'}</Link>
          <Link to="/guides">{lang === 'en' ? 'Articles' : '文章'}</Link>
          <Link to="/video">{tx(L.navVideo, lang)}</Link>
          <Link to="/paper">{tx(PX.nav, lang)}</Link>
          <Link to="/partners">{tx(PT.nav, lang)}</Link>
          <Link to="/privacy">{lang === 'en' ? 'Privacy' : lang === 'zh-HK' ? '私隱政策' : '隐私政策'}</Link>
          <Link to="/p/home">{tx(L.enter, lang)}</Link>
          <Link to={signup}>{tx(L.signin, lang)}</Link>
        </span>
      </footer>
    </div>
  );
}
